/**
 * Kind Tree Report - Simple at-a-glance strain/dispensary view
 */

import './styles/main.css';
import {
  initDatabase,
  getProducts,
  getLastUpdated,
  getRecentActivity,
  getActiveSales,
  getUpcomingPredictions,
  getDispensaryStats,
  getStrainBackInStock,
  getStrainFirstSeen,
} from './db';
import type { Product, LabData } from './types';
import { TERPENE_GLYPH_MAP } from './terpeneGlyphs';

type ViewMode = 'strain' | 'dispensary' | 'insights';

/**
 * Get current page from URL query parameter
 */
function getPageFromURL(): ViewMode {
  const params = new URLSearchParams(window.location.search);
  const page = params.get('page');

  // Validate and default to 'strain'
  if (page === 'dispensary' || page === 'insights') {
    return page;
  }
  return 'strain';
}

/**
 * Navigate to a page (update URL and render)
 */
function navigateToPage(page: ViewMode) {
  // Update URL
  const url = new URL(window.location.href);
  if (page === 'strain') {
    // Remove param for default view (cleaner URL)
    url.searchParams.delete('page');
  } else {
    url.searchParams.set('page', page);
  }

  // Push to history
  window.history.pushState({ page }, '', url);

  // Update state and render
  state.viewMode = page;
  render();
}

// Application state
const state = {
  products: [] as Product[],
  loading: true,
  viewMode: getPageFromURL() as ViewMode,
  backInStock: new Map<number, string>(),
  firstSeen: new Map<number, string>(),
  // Filter / sort state
  search: '',
  strainTypeFilter: '' as '' | 'sativa' | 'indica' | 'hybrid',
  sortMode: 'alpha' as 'alpha' | 'price' | 'distance' | 'terpene' | 'restock',
  sortAsc: true, // for alpha/price: true=ascending, false=descending
  terpeneFilter: 'Beta-Myrcene', // which terpene to sort by when sortMode==='terpene'
};

// ─── Location Service ────────────────────────────────────────────────────────

interface UserLocation {
  lat: number;
  lon: number;
  source: 'geolocation' | 'zipcode';
  label: string; // display text e.g. "ZIP 08540" or "Current Location"
}

function getUserLocation(): UserLocation | null {
  try {
    const stored = localStorage.getItem('kt_user_location');
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function setUserLocation(location: UserLocation) {
  localStorage.setItem('kt_user_location', JSON.stringify(location));
}

function clearUserLocation() {
  localStorage.removeItem('kt_user_location');
}

function toRad(deg: number): number {
  return deg * (Math.PI / 180);
}

/** Haversine straight-line distance in miles */
function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3959; // Earth radius in miles
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Estimate driving time: bird's-eye distance × 1.2 routing factor, 40 mph average */
function estimateDrivingMinutes(miles: number): number {
  return Math.round((miles * 1.2) / 40 * 60);
}

/** Get driving time from user location to a dispensary, or null if no data */
function getDrivingTime(dispensary: { latitude?: number; longitude?: number }): number | null {
  const userLoc = getUserLocation();
  if (!userLoc || !dispensary.latitude || !dispensary.longitude) return null;
  const miles = haversineDistance(userLoc.lat, userLoc.lon, dispensary.latitude, dispensary.longitude);
  return estimateDrivingMinutes(miles);
}

async function requestGeolocation(): Promise<UserLocation> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        lat: pos.coords.latitude,
        lon: pos.coords.longitude,
        source: 'geolocation',
        label: 'Current Location',
      }),
      (err) => reject(err),
      { timeout: 10000 }
    );
  });
}

async function geocodeZip(zip: string): Promise<UserLocation> {
  const url = `https://nominatim.openstreetmap.org/search?postalcode=${encodeURIComponent(zip)}&country=US&format=json&limit=1`;
  const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
  if (!res.ok) throw new Error('Geocoding request failed');
  const data = await res.json();
  if (!data || data.length === 0) throw new Error(`ZIP code ${zip} not found`);
  return {
    lat: parseFloat(data[0].lat),
    lon: parseFloat(data[0].lon),
    source: 'zipcode',
    label: `ZIP ${zip}`,
  };
}

// ─── Product Shop URL ────────────────────────────────────────────────────────

/**
 * Returns the deepest available product page URL.
 *
 * Priority:
 *   1. product.product_url — canonical URL stored by scraper (ADR-0013/0023)
 *   2. Client-side reconstruction from data_source (Dutchie, Dispense, Sweed)
 *   3. Shop/category page fallback (Blaze, QualityRoots)
 */
function getProductShopUrl(product: Product): string | null {
  // Prefer canonical URL from scraper (populated after ADR-0023 migration)
  if (product.product_url) return product.product_url;

  const disp = product.dispensary;
  if (!disp) return null;

  const { api_type, api_base_url, api_config } = disp;

  try {
    const cfg = api_config ? JSON.parse(api_config) : {};
    const ds = product.data_source ? JSON.parse(product.data_source) : {};

    switch (api_type) {
      case 'dutchie': {
        // Deep link: product._id from data_source
        const retailerId = cfg.retailer_id;
        const productId = ds.product?._id || ds.product?.id;
        if (retailerId && productId) {
          return `https://dutchie.com/dispensary/${retailerId}/product/${productId}`;
        }
        return retailerId ? `https://dutchie.com/dispensary/${retailerId}` : null;
      }

      case 'dispense': {
        // Deep link: product.slug from data_source
        const slug = ds.product?.slug;
        if (slug && api_base_url) {
          return `${api_base_url}/menu/products/${slug}`;
        }
        return api_base_url || null;
      }

      case 'sweed':
        // Apothecarium has a public Sweed shop; ZenLeaf's URL is an internal backend
        if (api_base_url.includes('shop.apothecarium.com')) return api_base_url;
        return null;

      case 'blaze':
        return api_base_url || null;

      case 'qualityroots':
        return cfg.url_pattern || api_base_url || null;

      default:
        return api_base_url || null;
    }
  } catch {
    return null;
  }
}

// ─── Back in Stock / First Seen ──────────────────────────────────────────────

/**
 * Returns a human-readable relative date string like "Mar 6" or "2 days ago"
 */
function formatRelativeDate(isoStr: string): string {
  const d = new Date(isoStr);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;

  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Check if a product has valid terpenes data
 */
function hasValidTerpenes(product: Product): boolean {
  if (!product.lab_data?.terpenes) return false;

  try {
    const terpenes = JSON.parse(product.lab_data.terpenes);
    return Object.keys(terpenes).length > 0;
  } catch {
    return false;
  }
}

/**
 * Parse terpenes JSON and return top 3 by concentration
 */
/**
 * Check if terpene JSON string has any values
 */
function hasTerpenes(terpenesJson: string | undefined): boolean {
  if (!terpenesJson) return false;
  try {
    const t = JSON.parse(terpenesJson);
    return Object.keys(t).length > 0;
  } catch {
    return false;
  }
}

/**
 * Build a map of strain_id → best available LabData with terpenes across all products.
 * Used as a cross-dispensary fallback when a product has no terpene data of its own.
 */
function buildStrainTerpeneFallback(products: Product[]): Map<number, LabData> {
  const map = new Map<number, LabData>();
  for (const p of products) {
    const strainId = p.strain?.id;
    if (!strainId) continue;
    const actual = p.lab_data;
    const inferred = p.inferred_lab_data;
    const candidate = hasTerpenes(actual?.terpenes) ? actual : hasTerpenes(inferred?.terpenes) ? inferred : null;
    if (!candidate) continue;
    const existing = map.get(strainId);
    if (!existing) {
      map.set(strainId, candidate);
    } else {
      try {
        const existingCount = Object.keys(JSON.parse(existing.terpenes || '{}')).length;
        const newCount = Object.keys(JSON.parse(candidate.terpenes || '{}')).length;
        if (newCount > existingCount) map.set(strainId, candidate);
      } catch { /* skip */ }
    }
  }
  return map;
}

/**
 * Merge cannabinoid fields from actual + inferred lab rows.
 * Actual values take precedence; inferred fills null/undefined gaps.
 */
function mergeLabCannabinoids(actual: LabData | undefined, inferred: LabData | undefined): LabData | undefined {
  if (!inferred) return actual;
  if (!actual) return inferred;
  return {
    ...actual,
    total_thc: actual.total_thc ?? inferred.total_thc,
    total_cbd: actual.total_cbd ?? inferred.total_cbd,
    cbd: actual.cbd ?? inferred.cbd,
    cbn: actual.cbn ?? inferred.cbn,
    cbg: actual.cbg ?? inferred.cbg,
    cbga: actual.cbga ?? inferred.cbga,
  };
}

/**
 * Merge two terpene JSON strings: actual values take precedence,
 * inferred fills in terpenes not present in actual.
 * Returns merged JSON sorted by descending value, or undefined if both empty.
 */
function mergeTerpeneJson(actualJson: string | undefined, inferredJson: string | undefined): string | undefined {
  const actual = (() => { try { return JSON.parse(actualJson || '{}'); } catch { return {}; } })();
  const inferred = (() => { try { return JSON.parse(inferredJson || '{}'); } catch { return {}; } })();
  const merged: Record<string, number> = { ...actual };
  for (const [key, val] of Object.entries(inferred)) {
    if (!(key in merged)) merged[key] = val as number;
  }
  if (Object.keys(merged).length === 0) return undefined;
  const sorted = Object.entries(merged).sort(([, a], [, b]) => (b as number) - (a as number));
  return JSON.stringify(Object.fromEntries(sorted));
}

/**
 * Get effective lab data for display.
 * Tier 1: actual terpenes only (complete panel)
 * Tier 1b: actual terpenes + inferred extension (when actual is partial — has some but inferred has more)
 * Tier 2: inferred terpenes only (actual is empty)
 * Tier 3: cross-dispensary fallback (no inference in DB, find same strain from other products)
 * Cannabinoids always come from actual lab data only.
 */
function getEffectiveLabData(product: Product, strainFallback?: Map<number, LabData>): {
  cannabinoidLab: LabData | undefined;
  effectiveTerpenes: string | undefined;
  isInferred: boolean;
  isCrossDispensary?: boolean;
  confidence?: number;
  thcDiff?: number;
} {
  const actual = product.lab_data;
  const inferred = product.inferred_lab_data;
  // Merge cannabinoids: actual takes precedence, inferred fills null gaps
  const cannabinoidLab = mergeLabCannabinoids(actual, inferred);

  const actualHas = hasTerpenes(actual?.terpenes);
  const inferredHas = hasTerpenes(inferred?.terpenes);

  // Tier 1: actual has terpenes — check if inferred can extend it
  if (actualHas) {
    // If inferred exists AND has more terpenes than actual, merge them
    if (inferredHas && inferred!.id !== actual!.id) {
      try {
        const actualCount = Object.keys(JSON.parse(actual!.terpenes!)).length;
        const inferredCount = Object.keys(JSON.parse(inferred!.terpenes!)).length;
        if (inferredCount > actualCount) {
          return {
            cannabinoidLab,
            effectiveTerpenes: mergeTerpeneJson(actual!.terpenes, inferred!.terpenes),
            isInferred: true,
            confidence: product.inferred_lab_data_confidence,
            thcDiff: product.inferred_lab_data_thc_diff,
          };
        }
      } catch { /* fall through */ }
    }
    return { cannabinoidLab, effectiveTerpenes: actual!.terpenes ?? undefined, isInferred: false };
  }

  // Tier 2: inferred only
  if (inferredHas) {
    return {
      cannabinoidLab,
      effectiveTerpenes: inferred!.terpenes ?? undefined,
      isInferred: true,
      confidence: product.inferred_lab_data_confidence,
      thcDiff: product.inferred_lab_data_thc_diff,
    };
  }

  // Tier 3: cross-dispensary
  if (strainFallback && product.strain?.id) {
    const fallback = strainFallback.get(product.strain.id);
    if (fallback?.terpenes) {
      return { cannabinoidLab, effectiveTerpenes: fallback.terpenes, isInferred: true, isCrossDispensary: true };
    }
  }

  return { cannabinoidLab, effectiveTerpenes: undefined, isInferred: false };
}

/**
 * Returns top terpenes as HTML string.
 * - Shows top 3 by default.
 * - If highlightTerpene is set and ranks beyond top 3, expands list to include it.
 * - The highlighted terpene is rendered bold.
 */
function getTopTerpenes(terpenesJson: string | undefined, highlightTerpene?: string): string {
  if (!terpenesJson) return '';

  try {
    const terpenes = JSON.parse(terpenesJson);
    const sorted = Object.entries(terpenes)
      .sort(([, a], [, b]) => (b as number) - (a as number));

    // Determine how many to show: at least min(3, total), extend to include highlight
    let showCount = Math.min(3, sorted.length);
    if (highlightTerpene) {
      const idx = sorted.findIndex(([name]) => name === highlightTerpene);
      if (idx >= 0) showCount = Math.max(showCount, idx + 1);
    }

    return sorted.slice(0, showCount).map(([name, value]) => {
      const glyph = TERPENE_GLYPH_MAP.get(name);
      const tooltip = glyph ? `${glyph.flavor} · ${glyph.effects}` : name;
      const glyphHtml = glyph
        ? `<span class="inline-block w-[13px] h-[13px] align-middle text-purple-300/50 -mt-0.5" style="vertical-align:middle">${glyph.svg.replace(/width="30" height="30"/, 'width="13" height="13"')}</span>`
        : '';
      const label = `${name} ${(value as number).toFixed(2)}%`;
      const inner = `${glyphHtml}${glyphHtml ? '&thinsp;' : ''}${label}`;
      if (highlightTerpene && name === highlightTerpene) {
        return `<strong class="text-purple-100 underline decoration-purple-300/60 underline-offset-2" title="${tooltip}">${inner}</strong>`;
      }
      return `<span title="${tooltip}">${inner}</span>`;
    }).join(', ');
  } catch {
    return '';
  }
}

/** Get a specific terpene's % value (max across products in the strain group) */
function getTerpeneValue(products: Product[], terpeneName: string): number {
  let max = 0;
  for (const p of products) {
    const { effectiveTerpenes } = getEffectiveLabData(p);
    if (!effectiveTerpenes) continue;
    try {
      const t = JSON.parse(effectiveTerpenes);
      const val = t[terpeneName] as number | undefined;
      if (val && val > max) max = val;
    } catch { /* skip */ }
  }
  return max;
}

/**
 * Format cannabinoids for display (only show non-zero values > 0.05%)
 */
function formatCannabinoids(lab: LabData | undefined): string {
  if (!lab) return '';

  const threshold = 0.05;
  const parts: string[] = [];

  // Always show THC if available
  if (lab.total_thc) {
    parts.push(`THC: ${lab.total_thc.toFixed(1)}%`);
  }

  // Show minor cannabinoids if above threshold
  if (lab.cbd && lab.cbd > threshold) {
    parts.push(`CBD: ${lab.cbd.toFixed(1)}%`);
  }
  if (lab.cbn && lab.cbn > threshold) {
    parts.push(`CBN: ${lab.cbn.toFixed(2)}%`);
  }
  if (lab.cbg && lab.cbg > threshold) {
    parts.push(`CBG: ${lab.cbg.toFixed(2)}%`);
  }
  if (lab.cbga && lab.cbga > threshold) {
    parts.push(`CBGA: ${lab.cbga.toFixed(2)}%`);
  }

  return parts.join(' ');
}

/**
 * Generate Google Maps URL for a dispensary
 */
function getGoogleMapsUrl(dispensary: { name: string; location: string; latitude?: number; longitude?: number }): string | null {
  if (!dispensary.latitude || !dispensary.longitude) {
    return null;
  }

  // Use coordinates for precise location
  return `https://www.google.com/maps/search/?api=1&query=${dispensary.latitude},${dispensary.longitude}`;
}

/**
 * Initialize the application
 */
async function init() {
  const app = document.getElementById('app');
  if (!app) return;

  // Show loading state
  app.innerHTML = `
    <div class="min-h-screen flex flex-col items-center justify-center" style="background: url('/assets/Kind-Tree_background.jpg') center/auto fixed; background-size: auto 100%;">
      <img src="/assets/KindTree-Logo-Header.svg" alt="Kind Tree" class="h-16 mb-6 opacity-90" />
      <div class="spinner mb-4"></div>
      <p class="text-lg text-white">Loading Kind Tree data...</p>
    </div>
  `;

  try {
    // Fetch version info
    let versionInfo = null;
    try {
      const versionResponse = await fetch('/version.json');
      if (versionResponse.ok) {
        versionInfo = await versionResponse.json();
      }
    } catch (e) {
      // Version info not critical, continue
    }

    await initDatabase();
    state.products = getProducts();
    state.backInStock = getStrainBackInStock();
    state.firstSeen = getStrainFirstSeen();

    // Log deployment info for debugging
    console.log('🌿 Kind Tree Report');
    if (versionInfo) {
      console.log('📝 Commit:', versionInfo.commitHash);
      console.log('🕐 Built:', new Date(versionInfo.buildDate).toLocaleString());
    }
    console.log('📊 Products loaded:', state.products.length);
    console.log('🏪 Dispensaries:', new Set(state.products.map(p => p.dispensary?.name)).size);
    console.log('🌱 Strains:', new Set(state.products.map(p => p.strain?.canonical_name)).size);
    console.log('📅 Last updated:', getLastUpdated());
    console.log('💾 Database URL:', '/data/kind_tree_data.db');

    state.loading = false;
    render();
  } catch (error) {
    state.loading = false;
    renderError(error instanceof Error ? error.message : 'Unknown error');
  }
}

/**
 * Render the main UI
 */
function render() {
  const app = document.getElementById('app');
  if (!app) return;

  app.innerHTML = `
    <div class="relative min-h-screen text-white">
      <!-- Fixed background: separate element avoids iOS Safari background-attachment:fixed bug -->
      <div class="fixed inset-0 -z-10" style="background: url('/assets/Kind-Tree_background.jpg') center/auto repeat; background-size: auto 100vh;"></div>
      <!-- Header -->
      <header class="border-b border-white/20 bg-purple-900/80 backdrop-blur">
        <div class="max-w-6xl mx-auto px-4 py-3">
          <!-- Row 1: Logo + Location -->
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-center gap-3 min-w-0">
              <img src="/assets/KindTree-Logo-Header.svg" alt="Kind Tree" class="h-10 shrink-0" />
              <p class="text-white/80 text-xs font-medium hidden sm:block whitespace-nowrap">Inventory Report</p>
            </div>
            ${(() => {
              const loc = getUserLocation();
              return `
                <button
                  id="location-btn"
                  class="shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    loc
                      ? 'bg-green-700/60 text-green-200 hover:bg-green-600/60'
                      : 'bg-purple-700/60 text-purple-300 hover:bg-purple-600/60'
                  }"
                  title="${loc ? `Location: ${loc.label} — click to change` : 'Set your location for driving time estimates'}"
                >
                  ${loc ? `📍 ${loc.label}` : '📍 Set Location'}
                </button>
              `;
            })()}
          </div>
          <!-- Row 2: Nav buttons (full width, equal) -->
          <div class="grid grid-cols-3 gap-1.5">
            <button
              class="py-2 px-2 rounded-lg text-sm font-medium transition-all text-center ${
                state.viewMode === 'strain'
                  ? 'bg-white text-purple-900'
                  : 'bg-purple-700/80 text-purple-200 hover:bg-purple-600'
              }"
              data-page="strain"
            >By Strain</button>
            <button
              class="py-2 px-2 rounded-lg text-sm font-medium transition-all text-center ${
                state.viewMode === 'dispensary'
                  ? 'bg-white text-purple-900'
                  : 'bg-purple-700/80 text-purple-200 hover:bg-purple-600'
              }"
              data-page="dispensary"
            >By Dispensary</button>
            <button
              class="py-2 px-2 rounded-lg text-sm font-medium transition-all text-center ${
                state.viewMode === 'insights'
                  ? 'bg-white text-purple-900'
                  : 'bg-purple-700/80 text-purple-200 hover:bg-purple-600'
              }"
              data-page="insights"
            >Insights</button>
          </div>
        </div>
      </header>

      <!-- Main Content -->
      <main class="max-w-6xl mx-auto px-6 py-8">
        ${state.viewMode === 'strain' ? renderStrainView() : state.viewMode === 'dispensary' ? renderDispensaryView() : renderInsightsView()}
      </main>

      <!-- Location Modal -->
      <div id="location-modal" class="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 hidden flex items-center justify-center p-4">
        <div class="bg-purple-900 border border-purple-500/60 rounded-xl p-6 w-full max-w-sm shadow-2xl">
          <h3 class="text-lg font-bold text-white mb-4">Set Your Location</h3>

          <button
            id="geo-btn"
            class="w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-700 hover:bg-purple-600 text-white rounded-lg transition-colors mb-4 font-medium"
          >
            <span>📍</span> Use My Current Location
          </button>

          <div class="flex items-center gap-3 mb-4">
            <div class="flex-1 h-px bg-purple-600"></div>
            <span class="text-purple-400 text-sm">or</span>
            <div class="flex-1 h-px bg-purple-600"></div>
          </div>

          <div class="flex gap-2">
            <input
              id="zip-input"
              type="text"
              inputmode="numeric"
              pattern="[0-9]{5}"
              maxlength="5"
              placeholder="ZIP code (e.g. 08540)"
              class="flex-1 px-3 py-2 bg-purple-800/60 border border-purple-500/50 rounded-lg text-white placeholder-purple-400 text-sm focus:outline-none focus:border-purple-300"
            />
            <button
              id="zip-btn"
              class="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg transition-colors text-sm font-medium"
            >
              Go
            </button>
          </div>

          <p id="location-status" class="text-sm text-purple-400 mt-3 min-h-[1.25rem]"></p>

          <div class="flex gap-2 mt-4">
            <button
              id="location-clear-btn"
              class="flex-1 px-4 py-2 bg-red-900/40 hover:bg-red-800/60 text-red-300 rounded-lg transition-colors text-sm"
            >
              Clear Location
            </button>
            <button
              id="location-close-btn"
              class="flex-1 px-4 py-2 bg-purple-700/60 hover:bg-purple-600 text-purple-200 rounded-lg transition-colors text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <footer class="border-t border-purple-600 bg-purple-900/50 backdrop-blur mt-12">
        <div class="max-w-6xl mx-auto px-6 py-4 text-center text-sm text-purple-300">
          <p>Data from <a href="https://github.com/jlumbroso/kind-tree-data" class="underline hover:text-white" target="_blank">kind-tree-data</a></p>
          ${(() => {
            const lastUpdated = getLastUpdated();
            if (lastUpdated) {
              const date = new Date(lastUpdated);
              const formatted = date.toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
              });
              return `<p class="text-purple-400 text-xs mt-1">Last updated: ${formatted}</p>`;
            }
            return '';
          })()}
        </div>
      </footer>
    </div>
  `;

  attachEventListeners();
}

/**
 * Render strain-based view
 */
function renderFilterBar(): string {
  const types = [
    { key: '', label: 'All' },
    { key: 'sativa', label: 'S' },
    { key: 'indica', label: 'I' },
    { key: 'hybrid', label: 'H' },
  ];
  const sorts = [
    { key: 'alpha', label: 'A–Z' },
    { key: 'price', label: '$/½g' },
    { key: 'restock', label: 'New In' },
    { key: 'distance', label: '🚗' },
    { key: 'terpene', label: 'Terp' },
  ];

  const commonTerpenes = ['Beta-Myrcene', 'Limonene', 'Beta-Caryophyllene', 'Linalool', 'Humulene', 'Alpha-Pinene', 'Beta-Pinene', 'Terpinolene', 'Bisabolol'];

  return `
    <div class="flex flex-wrap gap-2 mb-4 items-center">
      <!-- Search with clear -->
      <div class="relative flex items-center">
        <input
          id="strain-search"
          type="text"
          autocomplete="off"
          autocorrect="off"
          autocapitalize="off"
          spellcheck="false"
          value="${state.search.replace(/"/g, '&quot;')}"
          placeholder="Search strains…"
          class="px-3 py-1.5 pr-7 bg-purple-800/60 border border-purple-500/50 rounded-lg text-white placeholder-purple-400 text-sm focus:outline-none focus:border-purple-300 w-36"
        />
        ${state.search ? `<button id="search-clear" class="absolute right-2 text-purple-400 hover:text-white text-sm leading-none">✕</button>` : ''}
      </div>
      <!-- Type filter -->
      <div class="flex gap-1">
        ${types.map(t => `
          <button data-type-filter="${t.key}"
            class="px-2.5 py-1.5 rounded-lg text-sm font-medium transition-all ${
              state.strainTypeFilter === t.key
                ? 'bg-white text-purple-900'
                : 'bg-purple-700/60 text-purple-300 hover:bg-purple-600/60'
            }">
            ${t.label}
          </button>
        `).join('')}
      </div>
      <!-- Sort -->
      <div class="flex gap-1 ml-auto flex-wrap justify-end">
        ${sorts.map(s => {
          const isActive = state.sortMode === s.key;
          // For reversible sorts, show direction arrow
          const dirArrow = isActive && (s.key === 'alpha' || s.key === 'price')
            ? (state.sortAsc ? ' ↑' : ' ↓')
            : '';
          return `
            <button data-sort="${s.key}"
              class="px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
                isActive
                  ? 'bg-purple-400/30 text-white ring-1 ring-purple-400/50'
                  : 'bg-purple-700/40 text-purple-400 hover:bg-purple-600/40'
              }">
              ${s.label}${dirArrow}
            </button>
          `;
        }).join('')}
      </div>
    </div>
    ${state.sortMode === 'terpene' ? `
      <div class="flex flex-wrap gap-1 mb-4">
        <span class="text-purple-400 text-xs self-center mr-1">Sort by:</span>
        ${commonTerpenes.map(t => {
          const g = TERPENE_GLYPH_MAP.get(t);
          const iconHtml = g
            ? `<span class="inline-block w-[12px] h-[12px] align-middle opacity-70 -mt-0.5">${g.svg.replace(/width="30" height="30"/, 'width="12" height="12"')}</span>&thinsp;`
            : '';
          return `
          <button data-terpene="${t}"
            class="px-2 py-1 rounded text-xs transition-all ${
              state.terpeneFilter === t
                ? 'bg-purple-400/40 text-white ring-1 ring-purple-300/50'
                : 'bg-purple-800/60 text-purple-400 hover:bg-purple-700/60'
            }" title="${g ? `${g.flavor} · ${g.effects}` : t}">
            ${iconHtml}${t.replace('Beta-', 'β-').replace('Alpha-', 'α-')}
          </button>
        `}).join('')}
      </div>
    ` : ''}
  `;
}

function renderStrainView(): string {
  // Group products by strain
  const strainGroups = new Map<string, Product[]>();

  state.products.forEach(product => {
    const strainName = product.strain?.canonical_name || 'Unknown';
    if (!strainGroups.has(strainName)) {
      strainGroups.set(strainName, []);
    }
    strainGroups.get(strainName)!.push(product);
  });

  // Apply search and type filters
  let filtered = Array.from(strainGroups.entries()).filter(([strainName, products]) => {
    const strain = products[0].strain!;
    if (state.search && !strainName.toLowerCase().includes(state.search.toLowerCase())) return false;
    if (state.strainTypeFilter && strain.strain_type !== state.strainTypeFilter) return false;
    return true;
  });

  // Sort
  const userLoc = getUserLocation();
  const dir = state.sortAsc ? 1 : -1;
  filtered.sort(([nameA, prodsA], [nameB, prodsB]) => {
    switch (state.sortMode) {
      case 'price': {
        const bestA = Math.min(...prodsA.map(p => (p.current_price || 999) / (p.size_g / 0.5)));
        const bestB = Math.min(...prodsB.map(p => (p.current_price || 999) / (p.size_g / 0.5)));
        return dir * (bestA - bestB);
      }
      case 'restock': {
        const strainIdA = prodsA[0].strain!.id;
        const strainIdB = prodsB[0].strain!.id;
        const dateA = state.backInStock.get(strainIdA) || state.firstSeen.get(strainIdA) || '';
        const dateB = state.backInStock.get(strainIdB) || state.firstSeen.get(strainIdB) || '';
        return dateB.localeCompare(dateA); // Always most recent first
      }
      case 'distance': {
        if (!userLoc) return nameA.localeCompare(nameB);
        const minTimeA = Math.min(...prodsA.map(p => getDrivingTime(p.dispensary!) ?? 9999));
        const minTimeB = Math.min(...prodsB.map(p => getDrivingTime(p.dispensary!) ?? 9999));
        return minTimeA - minTimeB; // Always nearest first
      }
      case 'terpene': {
        // Sort by selected terpene concentration (highest first)
        const valA = getTerpeneValue(prodsA, state.terpeneFilter);
        const valB = getTerpeneValue(prodsB, state.terpeneFilter);
        return valB - valA;
      }
      default: // alpha
        return dir * nameA.localeCompare(nameB);
    }
  });

  const sortedStrains = filtered;

  return `
    <div class="space-y-6">
      <h2 class="text-2xl font-bold">Available Kind Tree Strains</h2>
      ${renderFilterBar()}
      ${sortedStrains.length === 0 ? `<p class="text-purple-400 text-center py-8">No strains match your filters.</p>` : ''}
      ${sortedStrains.map(([strainName, products]) => {
        const strain = products[0].strain!;
        // Find product with best effective terpenes (actual > inferred > any lab)
        const productWithLab = products.find(p => hasValidTerpenes(p))
          || products.find(p => p.inferred_lab_data && hasTerpenes(p.inferred_lab_data.terpenes))
          || products.find(p => p.lab_data)
          || products[0];
        const { cannabinoidLab: strainCannabinoidLab, effectiveTerpenes: strainTerpenes } = getEffectiveLabData(productWithLab);
        const strainType = strain.strain_type.charAt(0).toUpperCase();

        // Back-in-stock / first-seen date
        const backInStockDate = state.backInStock.get(strain.id);
        const firstSeenDate = state.firstSeen.get(strain.id);
        const stockDateStr = backInStockDate
          ? `↩ back ${formatRelativeDate(backInStockDate)}`
          : firstSeenDate
            ? `✦ first seen ${formatRelativeDate(firstSeenDate)}`
            : '';

        // Find best price (per 0.5g)
        const bestPrice = Math.min(...products.map(p => (p.current_price || 999) / (p.size_g / 0.5)));

        return `
          <div class="bg-purple-800/30 backdrop-blur rounded-lg p-5 border border-purple-600/50">
            <!-- Strain Header -->
            <div class="mb-3">
              <div class="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-1">
                <h3 class="text-xl font-bold text-white">${strainName}</h3>
                <span class="text-purple-300 text-sm">[${strainType}]</span>
                <span class="text-purple-200 text-sm">— best $${bestPrice.toFixed(2)}/½g</span>
                ${stockDateStr ? `<span class="text-yellow-400/80 text-xs">${stockDateStr}</span>` : ''}
              </div>
              ${(() => {
                const cannabinoids = formatCannabinoids(strainCannabinoidLab);
                const highlightTerpene = state.sortMode === 'terpene' ? state.terpeneFilter : undefined;
                const terpenes = getTopTerpenes(strainTerpenes, highlightTerpene);
                if (!cannabinoids && !terpenes) return '';
                return `
                  <div class="text-sm text-purple-200 font-mono">
                    ${cannabinoids}${terpenes ? ' | ' + terpenes : ''}
                  </div>
                `;
              })()}
            </div>

            <!-- Dispensaries -->
            <div class="pl-2 md:pl-4 space-y-2">
              ${([...products].sort((a, b) => {
                if (state.sortMode === 'distance') {
                  const tA = getDrivingTime(a.dispensary!) ?? 9999;
                  const tB = getDrivingTime(b.dispensary!) ?? 9999;
                  return tA - tB;
                }
                // Default: cheapest per-½g first
                const ppA = (a.current_price || 999) / (a.size_g / 0.5);
                const ppB = (b.current_price || 999) / (b.size_g / 0.5);
                return ppA - ppB;
              })).map(p => {
                const disp = p.dispensary!;
                const mapsUrl = getGoogleMapsUrl(disp);
                const shopUrl = getProductShopUrl(p);
                const drivingTime = getDrivingTime(disp);
                const price = p.current_price || 0;
                const pricePerHalf = (price / (p.size_g / 0.5)).toFixed(2);
                const isBestPrice = parseFloat(pricePerHalf) === bestPrice;
                const onSale = p.is_on_sale;
                const qty = p.available_quantity || 0;

                return `
                  <div class="text-sm">
                    <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span class="text-purple-200 font-medium min-w-0 break-words">
                        ${shopUrl ? `<a href="${shopUrl}" target="_blank" rel="noopener noreferrer" class="text-purple-300 hover:text-white transition-colors" title="Shop at ${disp.name}">🔗</a> ` : ''}${disp.name} — ${disp.location}${mapsUrl ? ` <a href="${mapsUrl}" target="_blank" rel="noopener noreferrer" class="text-purple-400 hover:text-purple-200 transition-colors" title="Open in Google Maps">📍</a>` : ''}
                      </span>
                      ${drivingTime !== null ? `<span class="text-green-400 text-xs whitespace-nowrap" title="~${drivingTime} min driving (estimated)">🚗 ~${drivingTime} min</span>` : ''}
                      <span class="text-purple-300 font-mono whitespace-nowrap">${p.size_g}g • $${price.toFixed(0)} [$${pricePerHalf}]${isBestPrice ? ' *' : ''}</span>
                      ${qty > 0 ? `<span class="text-purple-400 text-xs whitespace-nowrap">(${qty})</span>` : ''}
                      ${onSale ? '<span class="px-2 py-0.5 bg-orange-500/20 text-orange-300 text-xs rounded-full whitespace-nowrap">SALE</span>' : ''}
                    </div>
                  </div>
                `;
              }).join('')}
            </div>
          </div>
        `;
      }).join('')}
    </div>
  `;
}

/**
 * Render dispensary-based view
 */
function renderDispensaryView(): string {
  // Pre-build cross-dispensary terpene fallback (strain_id → best available LabData)
  const strainFallback = buildStrainTerpeneFallback(state.products);

  // Group products by dispensary, applying strain filters
  const dispensaryGroups = new Map<string, Product[]>();

  state.products.forEach(product => {
    const strain = product.strain;
    if (state.search && !strain?.canonical_name.toLowerCase().includes(state.search.toLowerCase())) return;
    if (state.strainTypeFilter && strain?.strain_type !== state.strainTypeFilter) return;

    const dispKey = `${product.dispensary?.name} - ${product.dispensary?.location}`;
    if (!dispensaryGroups.has(dispKey)) {
      dispensaryGroups.set(dispKey, []);
    }
    dispensaryGroups.get(dispKey)!.push(product);
  });

  // Sort dispensaries by active sort mode
  const userLoc = getUserLocation();
  const dispDir = state.sortAsc ? 1 : -1;
  const sortedDispensaries = Array.from(dispensaryGroups.entries()).sort((a, b) => {
    const prodsA = a[1], prodsB = b[1];
    switch (state.sortMode) {
      case 'distance': {
        if (!userLoc) return a[0].localeCompare(b[0]);
        const tA = getDrivingTime(prodsA[0]?.dispensary!) ?? 9999;
        const tB = getDrivingTime(prodsB[0]?.dispensary!) ?? 9999;
        return tA - tB; // Always nearest first
      }
      case 'price': {
        const minA = Math.min(...prodsA.map(p => (p.current_price || 999) / (p.size_g / 0.5)));
        const minB = Math.min(...prodsB.map(p => (p.current_price || 999) / (p.size_g / 0.5)));
        return dispDir * (minA - minB);
      }
      case 'terpene': {
        const valA = Math.max(...prodsA.map(p => {
          const { effectiveTerpenes } = getEffectiveLabData(p);
          try { return (JSON.parse(effectiveTerpenes || '{}')[state.terpeneFilter] as number) || 0; } catch { return 0; }
        }));
        const valB = Math.max(...prodsB.map(p => {
          const { effectiveTerpenes } = getEffectiveLabData(p);
          try { return (JSON.parse(effectiveTerpenes || '{}')[state.terpeneFilter] as number) || 0; } catch { return 0; }
        }));
        return valB - valA; // Always highest first
      }
      case 'restock': {
        const latestA = prodsA.reduce((best, p) => {
          const d = state.backInStock.get(p.strain!.id) || state.firstSeen.get(p.strain!.id) || '';
          return d > best ? d : best;
        }, '');
        const latestB = prodsB.reduce((best, p) => {
          const d = state.backInStock.get(p.strain!.id) || state.firstSeen.get(p.strain!.id) || '';
          return d > best ? d : best;
        }, '');
        return latestB.localeCompare(latestA); // Most recently restocked first
      }
      default: // alpha
        return dispDir * a[0].localeCompare(b[0]);
    }
  });

  return `
    <div class="space-y-6">
      <h2 class="text-2xl font-bold">Available by Dispensary</h2>
      ${renderFilterBar()}
      ${sortedDispensaries.map(([dispName, products]) => {
        const dispensary = products[0]?.dispensary;
        const mapsUrl = dispensary ? getGoogleMapsUrl(dispensary) : null;
        const drivingTime = dispensary ? getDrivingTime(dispensary) : null;

        return `
          <div class="bg-purple-800/30 backdrop-blur rounded-lg p-4 md:p-5 border border-purple-600/50">
            <!-- Dispensary Header -->
            <div class="mb-3">
              <div class="flex items-start gap-2">
                <h3 class="text-lg md:text-xl font-bold text-white break-words flex-1">
                  ${dispName}${drivingTime !== null ? ` <span class="text-green-400 text-sm font-normal" title="~${drivingTime} min driving (estimated)">🚗 ~${drivingTime} min</span>` : ''}
                </h3>
                ${mapsUrl ? `
                  <a href="${mapsUrl}" target="_blank" rel="noopener noreferrer"
                     class="text-purple-300 hover:text-purple-100 transition-colors"
                     title="Open in Google Maps">
                    <span class="text-xl">📍</span>
                  </a>
                ` : ''}
              </div>
              <span class="text-purple-400 text-xs md:text-sm">${products.length} product${products.length !== 1 ? 's' : ''}</span>
            </div>

            <!-- Products -->
            <div class="pl-2 md:pl-4 space-y-3">
              ${([...products].sort((a, b) => {
                if (state.sortMode === 'price') {
                  const ppA = (a.current_price || 999) / (a.size_g / 0.5);
                  const ppB = (b.current_price || 999) / (b.size_g / 0.5);
                  return state.sortAsc ? ppA - ppB : ppB - ppA;
                }
                // Default + A-Z: alphabetical by strain name
                const cmp = (a.strain?.canonical_name || '').localeCompare(b.strain?.canonical_name || '');
                return state.sortAsc ? cmp : -cmp;
              })).map(p => {
                const strain = p.strain!;
                const { cannabinoidLab, effectiveTerpenes, isInferred, isCrossDispensary, confidence, thcDiff } = getEffectiveLabData(p, strainFallback);
                const strainType = strain.strain_type.charAt(0).toUpperCase();
                const price = p.current_price || 0;
                const pricePerHalf = (price / (p.size_g / 0.5)).toFixed(2);
                const onSale = p.is_on_sale;
                const qty = p.available_quantity || 0;

                const shopUrl = getProductShopUrl(p);

                return `
                  <div class="text-sm">
                    <div class="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1">
                      ${shopUrl ? `<a href="${shopUrl}" target="_blank" rel="noopener noreferrer" class="text-purple-300 hover:text-white transition-colors" title="Shop at ${p.dispensary?.name}">🔗</a>` : ''}
                      <button class="text-white font-semibold hover:text-purple-300 transition-colors text-left" data-filter-strain="${strain.canonical_name}" title="Show only ${strain.canonical_name}">${strain.canonical_name}</button>
                      <span class="text-purple-400 text-xs">[${strainType}]</span>
                      <span class="text-purple-300 font-mono whitespace-nowrap">${p.size_g}g • $${price.toFixed(0)} [$${pricePerHalf}]</span>
                      ${qty > 0 ? `<span class="text-purple-400 text-xs whitespace-nowrap">(${qty})</span>` : ''}
                      ${onSale ? '<span class="px-2 py-0.5 bg-orange-500/20 text-orange-300 text-xs rounded-full whitespace-nowrap">SALE</span>' : ''}
                    </div>
                    ${(() => {
                      const cannabinoids = formatCannabinoids(cannabinoidLab);
                      const highlightTerpene = state.sortMode === 'terpene' ? state.terpeneFilter : undefined;
                      const terpenes = getTopTerpenes(effectiveTerpenes, highlightTerpene);
                      if (!cannabinoids && !terpenes) return '';

                      // Build tooltip for inferred terpenes
                      const tooltip = isCrossDispensary
                        ? 'Terpene profile from another dispensary carrying this strain (scraper did not return terpenes here)'
                        : isInferred && confidence !== undefined
                          ? `Terpene data inferred from similar batch (confidence: ${Math.round(confidence * 100)}%, THC diff: ${Math.abs(thcDiff || 0).toFixed(2)}%)`
                          : '';

                      // Wrap terpenes in span with opacity if inferred
                      const terpeneDisplay = terpenes
                        ? ((isInferred || isCrossDispensary)
                            ? `<span class="opacity-60" title="${tooltip}">${terpenes}</span>`
                            : terpenes)
                        : '';

                      return `
                        <div class="text-xs text-purple-300 font-mono pl-0 md:pl-4">
                          ${cannabinoids}${terpeneDisplay ? ' | ' + terpeneDisplay : ''}
                        </div>
                      `;
                    })()}
                  </div>
                `;
              }).join('')}
            </div>
          </div>
        `;
      }).join('')}
    </div>
  `;
}

/**
 * Attach event listeners
 */
function attachEventListeners() {
  document.querySelectorAll('[data-page]').forEach(button => {
    button.addEventListener('click', (e) => {
      const page = (e.target as HTMLElement).getAttribute('data-page') as ViewMode;
      navigateToPage(page);
    });
  });

  // Filter bar — search (debounced; restores focus+cursor after re-render)
  let searchTimer: ReturnType<typeof setTimeout> | null = null;
  const searchInput = document.getElementById('strain-search') as HTMLInputElement | null;
  if (searchInput) {
    // Prevent mobile zoom (iOS zooms in when input font-size < 16px)
    searchInput.style.fontSize = '16px';
    searchInput.addEventListener('input', () => {
      state.search = searchInput.value;
      const cursorPos = searchInput.selectionStart ?? searchInput.value.length;
      if (searchTimer) clearTimeout(searchTimer);
      searchTimer = setTimeout(() => {
        render();
        const newInput = document.getElementById('strain-search') as HTMLInputElement | null;
        if (newInput) {
          newInput.style.fontSize = '16px';
          newInput.focus();
          newInput.setSelectionRange(cursorPos, cursorPos);
        }
      }, 180);
    });
  }

  // Filter bar — clear search button
  document.getElementById('search-clear')?.addEventListener('click', () => {
    state.search = '';
    render();
    const newInput = document.getElementById('strain-search') as HTMLInputElement | null;
    if (newInput) { newInput.style.fontSize = '16px'; newInput.focus(); }
  });

  // Filter bar — type toggle
  document.querySelectorAll('[data-type-filter]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const val = (e.currentTarget as HTMLElement).getAttribute('data-type-filter') as '' | 'sativa' | 'indica' | 'hybrid';
      state.strainTypeFilter = val;
      render();
    });
  });

  // Filter bar — sort (clicking active alpha/price toggles direction)
  document.querySelectorAll('[data-sort]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const val = (e.currentTarget as HTMLElement).getAttribute('data-sort') as typeof state.sortMode;
      if (val === state.sortMode && (val === 'alpha' || val === 'price')) {
        state.sortAsc = !state.sortAsc;
      } else {
        state.sortMode = val;
        state.sortAsc = true;
      }
      render();
    });
  });

  // Filter bar — terpene selector
  document.querySelectorAll('[data-terpene]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      state.terpeneFilter = (e.currentTarget as HTMLElement).getAttribute('data-terpene') || 'Beta-Myrcene';
      render();
    });
  });

  // Dispensary view: clicking a strain name filters and switches to strain view
  document.querySelectorAll('[data-filter-strain]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const strainName = (e.currentTarget as HTMLElement).getAttribute('data-filter-strain') || '';
      state.search = strainName;
      navigateToPage('strain');
    });
  });

  // Location modal
  const modal = document.getElementById('location-modal');
  const status = document.getElementById('location-status');

  const showModal = () => modal?.classList.remove('hidden');
  const hideModal = () => modal?.classList.add('hidden');
  const setStatus = (msg: string, isError = false) => {
    if (status) {
      status.textContent = msg;
      status.className = `text-sm mt-3 min-h-[1.25rem] ${isError ? 'text-red-400' : 'text-green-400'}`;
    }
  };

  document.getElementById('location-btn')?.addEventListener('click', showModal);
  document.getElementById('location-close-btn')?.addEventListener('click', hideModal);
  modal?.addEventListener('click', (e) => { if (e.target === modal) hideModal(); });

  document.getElementById('location-clear-btn')?.addEventListener('click', () => {
    clearUserLocation();
    hideModal();
    render();
  });

  document.getElementById('geo-btn')?.addEventListener('click', async () => {
    const btn = document.getElementById('geo-btn') as HTMLButtonElement;
    btn.disabled = true;
    btn.textContent = '⏳ Getting location...';
    setStatus('');
    try {
      const loc = await requestGeolocation();
      setUserLocation(loc);
      setStatus('✓ Location set!');
      setTimeout(() => { hideModal(); render(); }, 700);
    } catch {
      setStatus('Could not get location. Check browser permissions.', true);
    } finally {
      btn.disabled = false;
      btn.innerHTML = '<span>📍</span> Use My Current Location';
    }
  });

  const zipInput = document.getElementById('zip-input') as HTMLInputElement | null;
  const zipBtn = document.getElementById('zip-btn');

  const handleZip = async () => {
    const zip = zipInput?.value.trim() || '';
    if (!/^\d{5}$/.test(zip)) {
      setStatus('Please enter a valid 5-digit ZIP code.', true);
      return;
    }
    if (zipBtn) (zipBtn as HTMLButtonElement).disabled = true;
    setStatus('Looking up ZIP code...');
    try {
      const loc = await geocodeZip(zip);
      setUserLocation(loc);
      setStatus('✓ Location set!');
      setTimeout(() => { hideModal(); render(); }, 700);
    } catch {
      setStatus(`ZIP code ${zip} not found.`, true);
    } finally {
      if (zipBtn) (zipBtn as HTMLButtonElement).disabled = false;
    }
  };

  zipBtn?.addEventListener('click', handleZip);
  zipInput?.addEventListener('keydown', (e) => { if (e.key === 'Enter') handleZip(); });
}

// Handle browser back/forward navigation
window.addEventListener('popstate', (event) => {
  const page = event.state?.page || getPageFromURL();
  state.viewMode = page;
  render();
});

/**
 * Format date/time for display
 */
function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }

  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Format future date as "in X days"
 */
function formatFutureDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays < 7) return `in ${diffDays}d`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Render insights view (Option A: Activity-Focused)
 */
function renderInsightsView(): string {
  try {
    const recentActivity = getRecentActivity(10);
    const activeSales = getActiveSales();
    const predictions = getUpcomingPredictions();
    const dispensaryStats = getDispensaryStats();

    return `
      <div class="space-y-6">
        <h2 class="text-2xl font-bold mb-6">Insights</h2>

        ${renderRecentActivity(recentActivity)}
        ${renderActiveSales(activeSales)}
        ${renderUpcomingPredictions(predictions)}
        ${renderDispensaryRankings(dispensaryStats)}
      </div>
    `;
  } catch (error) {
    console.error('Error rendering insights:', error);
    return `
      <div class="space-y-6">
        <h2 class="text-2xl font-bold mb-6">Insights</h2>
        <div class="bg-purple-800/30 backdrop-blur rounded-lg p-6 border border-purple-600/50">
          <p class="text-purple-200 text-center">
            ⚠️ Unable to load insights data
            <br/>
            <span class="text-sm text-purple-300 mt-2 block">
              ${error instanceof Error ? error.message : 'Unknown error'}
            </span>
          </p>
        </div>
      </div>
    `;
  }
}

/**
 * Render recent activity feed
 */
function renderRecentActivity(activities: any[]): string {
  if (!activities || activities.length === 0) {
    return `
      <div class="bg-purple-800/30 backdrop-blur rounded-lg p-6 border border-purple-600/50">
        <h3 class="text-lg font-semibold mb-4 text-purple-100">🔔 Recent Activity (24h)</h3>
        <p class="text-purple-300 text-sm text-center">No recent activity</p>
      </div>
    `;
  }

  return `
    <div class="bg-purple-800/30 backdrop-blur rounded-lg p-5 border border-purple-600/50">
      <h3 class="text-lg font-semibold mb-4 text-purple-100">🔔 Recent Activity (24h)</h3>
      <div class="space-y-3">
        ${activities.map(activity => {
          const emoji = activity.event_type === 'sale'
            ? (activity.sale_type === 'start' ? '🔖' : '🏷️')
            : activity.event_type === 'restock' ? '📦' : '⚠️';

          const label = activity.event_type === 'sale'
            ? (activity.sale_type === 'start' ? 'Sale started' : 'Sale ended')
            : activity.event_type === 'restock' ? 'Restocked' : 'Sold out';

          const detail = activity.event_type === 'sale' && activity.discount_pct
            ? ` (-${Math.round(activity.discount_pct * 100)}%)`
            : activity.event_type === 'restock' && activity.discount_pct
            ? ` (+${activity.discount_pct})`
            : '';

          return `
            <div class="flex items-start gap-3 text-sm">
              <span class="text-lg">${emoji}</span>
              <div class="flex-1">
                <div class="text-purple-100 font-medium">
                  ${label}${detail}
                </div>
                <div class="text-purple-300">
                  ${activity.strain_name} at ${activity.dispensary_name}
                </div>
              </div>
              <span class="text-purple-400 text-xs whitespace-nowrap">
                ${formatDateTime(activity.detected_at)}
              </span>
            </div>
          `;
        }).join('')}
      </div>
    </div>
  `;
}

/**
 * Render active sales
 */
function renderActiveSales(sales: any[]): string {
  if (!sales || sales.length === 0) {
    return `
      <div class="bg-purple-800/30 backdrop-blur rounded-lg p-6 border border-purple-600/50">
        <h3 class="text-lg font-semibold mb-4 text-purple-100">💰 Active Sales</h3>
        <p class="text-purple-300 text-sm text-center">No active sales right now</p>
      </div>
    `;
  }

  return `
    <div class="bg-purple-800/30 backdrop-blur rounded-lg p-5 border border-purple-600/50">
      <h3 class="text-lg font-semibold mb-4 text-purple-100">💰 Active Sales (${sales.length})</h3>
      <div class="space-y-2">
        ${sales.slice(0, 8).map(sale => `
          <div class="flex items-center justify-between text-sm py-2 border-b border-purple-700/30 last:border-0">
            <div class="flex-1">
              <span class="text-purple-100 font-medium">${sale.strain_name}</span>
              <span class="text-purple-400 mx-2">•</span>
              <span class="text-purple-300">${sale.dispensary_name}${sale.dispensary_location ? ` (${sale.dispensary_location})` : ''}</span>
              <span class="text-purple-400 mx-2 text-xs">${sale.size_g}g</span>
            </div>
            <div class="flex items-center gap-3">
              ${sale.discount_pct ? `
                <span class="text-yellow-400 font-semibold">-${Math.round(sale.discount_pct * 100)}%</span>
              ` : ''}
              <span class="text-purple-200 font-mono">$${sale.current_price?.toFixed(2) || 'N/A'}</span>
            </div>
          </div>
        `).join('')}
        ${sales.length > 8 ? `
          <p class="text-purple-400 text-xs text-center pt-2">
            +${sales.length - 8} more sales
          </p>
        ` : ''}
      </div>
    </div>
  `;
}

/**
 * Render upcoming predictions
 */
function renderUpcomingPredictions(predictions: any[]): string {
  if (!predictions || predictions.length === 0) {
    return `
      <div class="bg-purple-800/30 backdrop-blur rounded-lg p-6 border border-purple-600/50">
        <h3 class="text-lg font-semibold mb-4 text-purple-100">📅 Coming Soon (Next 7 Days)</h3>
        <p class="text-purple-300 text-sm text-center">No predictions available yet</p>
      </div>
    `;
  }

  // Group by prediction type
  const restocks = predictions.filter(p => p.prediction_type === 'next_restock');
  const sales = predictions.filter(p => p.prediction_type === 'next_sale');
  const stockouts = predictions.filter(p => p.prediction_type === 'stock_out');

  return `
    <div class="bg-purple-800/30 backdrop-blur rounded-lg p-5 border border-purple-600/50">
      <h3 class="text-lg font-semibold mb-4 text-purple-100">📅 Coming Soon (Next 7 Days)</h3>

      ${sales.length > 0 ? `
        <div class="mb-4">
          <h4 class="text-sm font-medium text-purple-200 mb-2">🔖 Predicted Sales</h4>
          <div class="space-y-2">
            ${sales.slice(0, 5).map(pred => `
              <div class="flex items-center justify-between text-sm">
                <div class="flex-1">
                  <span class="text-purple-100">${pred.strain_name}</span>
                  <span class="text-purple-400 text-xs ml-2">at ${pred.dispensary_name}</span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-purple-300 text-xs">${formatFutureDate(pred.predicted_date)}</span>
                  <span class="text-purple-400 text-xs">${Math.round(pred.confidence * 100)}%</span>
                </div>
              </div>
            `).join('')}
          </div>
        </div>
      ` : ''}

      ${restocks.length > 0 ? `
        <div class="mb-4">
          <h4 class="text-sm font-medium text-purple-200 mb-2">📦 Predicted Restocks</h4>
          <div class="space-y-2">
            ${restocks.slice(0, 5).map(pred => `
              <div class="flex items-center justify-between text-sm">
                <div class="flex-1">
                  <span class="text-purple-100">${pred.strain_name}</span>
                  <span class="text-purple-400 text-xs ml-2">at ${pred.dispensary_name}</span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-purple-300 text-xs">${formatFutureDate(pred.predicted_date)}</span>
                  <span class="text-purple-400 text-xs">${Math.round(pred.confidence * 100)}%</span>
                </div>
              </div>
            `).join('')}
          </div>
        </div>
      ` : ''}

      ${stockouts.length > 0 ? `
        <div>
          <h4 class="text-sm font-medium text-purple-200 mb-2">⚠️ Predicted Stock-Outs</h4>
          <div class="space-y-2">
            ${stockouts.slice(0, 5).map(pred => `
              <div class="flex items-center justify-between text-sm">
                <div class="flex-1">
                  <span class="text-purple-100">${pred.strain_name}</span>
                  <span class="text-purple-400 text-xs ml-2">at ${pred.dispensary_name}</span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-purple-300 text-xs">${formatFutureDate(pred.predicted_date)}</span>
                  <span class="text-purple-400 text-xs">${Math.round(pred.confidence * 100)}%</span>
                </div>
              </div>
            `).join('')}
            ${stockouts.length > 5 ? `
              <p class="text-purple-400 text-xs text-center pt-2">
                +${stockouts.length - 5} more predicted stock-outs
              </p>
            ` : ''}
          </div>
        </div>
      ` : ''}

      ${restocks.length === 0 && sales.length === 0 && stockouts.length === 0 ? `
        <p class="text-purple-300 text-sm text-center">No predictions for the next 7 days</p>
      ` : ''}
    </div>
  `;
}

/**
 * Render dispensary rankings
 */
function renderDispensaryRankings(stats: any[]): string {
  if (!stats || stats.length === 0) {
    return `
      <div class="bg-purple-800/30 backdrop-blur rounded-lg p-6 border border-purple-600/50">
        <h3 class="text-lg font-semibold mb-4 text-purple-100">🏪 Dispensary Activity (30d)</h3>
        <p class="text-purple-300 text-sm text-center">No statistics available yet</p>
      </div>
    `;
  }

  // Filter and sort dispensaries with data
  const rankedStats = stats
    .filter(s => s.avg_consumption_rate != null)
    .sort((a, b) => (b.avg_consumption_rate || 0) - (a.avg_consumption_rate || 0));

  if (rankedStats.length === 0) {
    return `
      <div class="bg-purple-800/30 backdrop-blur rounded-lg p-6 border border-purple-600/50">
        <h3 class="text-lg font-semibold mb-4 text-purple-100">🏪 Dispensary Activity (30d)</h3>
        <p class="text-purple-300 text-sm text-center">Collecting data...</p>
      </div>
    `;
  }

  return `
    <div class="bg-purple-800/30 backdrop-blur rounded-lg p-5 border border-purple-600/50">
      <h3 class="text-lg font-semibold mb-4 text-purple-100">🏪 Dispensary Activity (30d)</h3>
      <div class="space-y-3">
        ${rankedStats.map((stat, index) => `
          <div class="flex items-center justify-between text-sm">
            <div class="flex items-center gap-3 flex-1">
              <span class="text-purple-400 font-medium w-6">#${index + 1}</span>
              <div>
                <div class="text-purple-100 font-medium">${stat.name}</div>
                <div class="text-purple-400 text-xs">${stat.location}</div>
              </div>
            </div>
            <div class="text-right">
              <div class="text-purple-200 font-semibold">
                ${Math.round(stat.avg_consumption_rate)} units/day
                ${index === 0 ? ' 🔥' : ''}
              </div>
              ${stat.kind_tree_availability_rate != null ? `
                <div class="text-purple-400 text-xs">
                  ${Math.round(stat.kind_tree_availability_rate * 100)}% uptime
                </div>
              ` : ''}
            </div>
          </div>
        `).join('')}
      </div>
    </div>
  `;
}

/**
 * Render error state
 */
function renderError(message: string) {
  const app = document.getElementById('app');
  if (!app) return;

  app.innerHTML = `
    <div class="min-h-screen flex flex-col items-center justify-center" style="background: url('/assets/Kind-Tree_background.jpg') center/auto fixed; background-size: auto 100%;">
      <img src="/assets/KindTree-Logo-Header.svg" alt="Kind Tree" class="h-16 mb-6 opacity-90" />
      <div class="text-red-400 text-6xl mb-4">⚠️</div>
      <h1 class="text-2xl font-bold text-white mb-2">Failed to Load Data</h1>
      <p class="text-white/80 mb-4">${message}</p>
      <button class="px-6 py-3 bg-white text-purple-900 rounded-lg font-medium hover:bg-purple-100" onclick="location.reload()">
        Retry
      </button>
    </div>
  `;
}

// Start the application
init();
